/**
 * The right pane's JSON tab: the live design document, exactly as it will be
 * saved.
 *
 * Three things it is for:
 * - **read** — see what the canvas actually produced, scoped to the selection
 *   when you only care about one widget's payload;
 * - **take** — copy or download it;
 * - **write** — edit the text and Apply, which validates and replaces the
 *   document as a single undoable commit. Invalid JSON changes nothing.
 *
 * The textarea is only bound to the document while it is NOT being edited —
 * otherwise every keystroke would be overwritten by the live document.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { DesignDocumentStore } from '../../state/design-document.store';
import {
  parseDesignDocument,
  serializeDesignDocument,
  serializeNode,
  validateDocument,
} from '../../utils/design-json.utils';

@Component({
  selector: 'app-design-json-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './design-json-panel.component.html',
  styleUrl: './design-json-panel.component.scss',
})
export class DesignJsonPanelComponent {
  private readonly doc = inject(DesignDocumentStore);

  /** Narrow the view to the selected subtree. */
  readonly scoped = signal(false);
  /** Draft text while editing; null means "mirror the live document". */
  readonly draft = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly copied = signal(false);

  readonly isEditing = computed(() => this.draft() !== null);

  /** The live document (or selected subtree) as pretty JSON. */
  readonly liveJson = computed(() => {
    // Depend on the revision so this recomputes on every commit.
    this.doc.revision();
    const node = this.doc.selectedNode();
    return this.scoped() && node
      ? serializeNode(node)
      : serializeDesignDocument(this.doc.document());
  });

  /** What the textarea shows: the draft while editing, else the live document. */
  readonly text = computed(() => this.draft() ?? this.liveJson());

  readonly issues = computed(() => {
    this.doc.revision();
    return validateDocument(this.doc.document());
  });

  readonly sizeLabel = computed(() => {
    const bytes = new Blob([this.liveJson()]).size;
    return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
  });

  readonly nodeCount = computed(() => {
    this.doc.revision();
    const count = (nodes: readonly { children?: unknown[] }[]): number =>
      nodes.reduce(
        (sum, node) => sum + 1 + count((node.children ?? []) as { children?: unknown[] }[]),
        0,
      );
    return this.doc.document().pages.reduce((sum, page) => sum + count(page.nodes), 0);
  });

  // ── Actions ────────────────────────────────────────────────────────────────

  onTextChange(value: string): void {
    this.draft.set(value);
    this.error.set(null);
  }

  /** Discard the draft and snap back to the live document. */
  revert(): void {
    this.draft.set(null);
    this.error.set(null);
  }

  apply(): void {
    const draft = this.draft();
    if (draft === null) return;

    // Scoped edits would need a subtree merge; keep Apply to whole documents
    // so a partial paste can never silently drop the rest of the design.
    if (this.scoped()) {
      this.error.set('Switch off "Selection only" to apply an edited document.');
      return;
    }

    const { document, error } = parseDesignDocument(draft);
    if (!document) {
      this.error.set(error ?? 'Could not parse that JSON.');
      return;
    }
    this.doc.setDocument(document, 'Apply JSON');
    this.draft.set(null);
    this.error.set(null);
  }

  async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.liveJson());
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1600);
    } catch {
      this.error.set('Clipboard access was blocked by the browser.');
    }
  }

  download(): void {
    const document = this.doc.document();
    const blob = new Blob([serializeDesignDocument(document)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = `${document.slug || 'design'}.design.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  toggleScope(): void {
    this.scoped.update((value) => !value);
    // The scope change swaps what the textarea shows, so a draft is stale.
    this.revert();
  }
}
